import React, { useState } from 'react';
import { Mail, Phone, MapPin, Shield, Server, ChevronDown, ChevronUp, Linkedin, Cpu } from 'lucide-react';

const App = () => {
  const [expandedProject, setExpandedProject] = useState(null);

  const toggleProject = (index) => {
    setExpandedProject(expandedProject === index ? null : index);
  };

  const data = {
    name: "Kashif Alvi",
    title: "Sr. System Administrator | Network Engineer",
    contact: {
      phone: "+91-9773582894",
      email: "alvikashif29@gmail.com",
      location: "Mumbai Suburban",
      linkedin: "https://www.linkedin.com/in/kashif-alvi-8404b898/" // Update this with your actual URL
    },
    skills: ["Windows System Administration", "Redhat Linux", "Cisco Meraki", "Fortigate Firewall", "Oracle Cloud", "Network Engineering"],

    techStack: [
      { category: "Networking", tools: "Cisco Meraki, Fortigate SD-WAN, Cisco Switch, VLAN Segmentation, TCP/IP" },
      { category: "Servers/OS", tools: "Windows Server (AD/FS), RHEL, Oracle Linux, LAMP, Samba" },
      { category: "Cloud/SaaS", tools: "Oracle Cloud (OCI), Cloudflare Zero Trust, Zimbra, WHM" },
      { category: "Infrastructure", tools: "Datacenter Management, L2 Support, Backup Strategy (Rsync/Snapshots)" }
    ],
    experience: [
      {
        company: "Actis Technologies",
        role: "Sr. System Administrator",
        period: "May 2024 - Present",
        desc: "Managing IT infrastructure across India, including Cisco Meraki deployments and on-field network support."
      },
      {
        company: "NetGains Technologies",
        role: "Team Lead - Support & IT Infrastructure",
        period: "Jan 2024 - May 2024",
        desc: "Managed critical infrastructure within the Datacenter."
      },
      {
        company: "Halo Media Works",
        role: "System Administrator",
        period: "Feb 2018 - Jan 2024",
        desc: "Managed Windows domains, Linux Samba servers, Fortigate firewalls, and Oracle Cloud infrastructure."
      },
      {
        company: "Halo Media Works",
        role: "Desktop Support Engineer (L1 & L2)",
        period: "Jan 2015 - Feb 2018",
        desc: "Provided comprehensive end-user support and resolved complex L2 hardware/software escalations."
      }
    ],
    projects: [
      {
      title: "Cisco Meraki Deployment",
      date: "August 2024 - September 2024",
      short: "Full stack deployment for Actis's Bangalore office including VLAN segmentation.",
      details: [
        "Responsible for setting up the Cisco Meraki firewall with the ISP.",
        "Configured Meraki switches with VLANs to separate audio/video devices from access point networks.",
        "Ensured secure and reliable network infrastructure for the regional office."
      ]
    },
    {
      title: "Oracle Cloud Migration",
      date: "January 2021 - February 2021",
      short: "Deployed OES and FTP servers on Oracle Enterprise Linux for reliability and scalability.",
      details: [
        "Migrated services from several dedicated servers to a unified Oracle Cloud infrastructure for easier management.",
        "Deployed Order Entry System (OES) using a LAMP stack on Oracle Enterprise Linux.",
        "Configured proftpd for reliable FTP services within the cloud environment.",
        "Managed scaling of server resources on Oracle Cloud for cost-effectiveness."
      ]
    },
    {
      title: "Fortigate Firewall Implementation",
      date: "September 2018 - October 2018",
      short: "Part of the team providing secure and reliable internet to the organization.",
      details: [
        "Collaborated with vendor engineers to implement comprehensive firewall policies.",
        "Configured users, groups, Web Filters, and SDWAN for optimized traffic management.",
        "Set up proxy for users and managed both outbound and inbound firewall policies.",
        "Maintained services on WAN to ensure organizational security."
      ]
    }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <header className="bg-slate-900 text-white py-16 px-4 text-center md:text-left">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8">
          <div className="w-32 h-32 rounded-full bg-blue-500 border-4 border-slate-700 flex items-center justify-center text-3xl font-bold shadow-xl">
            KA
          </div>
          <div>
            <h1 className="text-4xl font-bold tracking-tight">{data.name}</h1>
            <p className="text-xl text-blue-400 mt-2 font-medium">{data.title}</p>
            <div className="flex flex-wrap justify-center md:justify-start gap-4 mt-4 text-slate-300 text-sm">
              <span className="flex items-center gap-1"><Phone size={14}/> {data.contact.phone}</span>
              <a href={`mailto:${data.contact.email}`} className="flex items-center gap-1 hover:text-blue-400 transition-colors">
                <Mail size={14}/> {data.contact.email}
              </a>
              <a href={data.contact.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-blue-400 transition-colors">
                <Linkedin size={14}/> LinkedIn
              </a>
              <span className="flex items-center gap-1"><MapPin size={14}/> {data.contact.location}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto py-12 px-4 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-1 space-y-8">
          <section>
            <h2 className="text-lg font-bold border-b-2 border-blue-500 pb-2 mb-4 uppercase tracking-wider text-slate-700">Technical Skills</h2>
            <div className="flex flex-wrap gap-2">
              {data.skills.map(skill => (
                <span key={skill} className="bg-white border border-slate-200 px-3 py-1 rounded-full text-sm font-medium text-slate-700 shadow-sm transition-transform hover:scale-105">
                  {skill}
                </span>
              ))}
            </div>
          </section>
          
          <section className="bg-blue-50 p-4 rounded-lg border border-blue-100 shadow-sm">
            <h2 className="text-lg font-bold text-blue-800 mb-2 uppercase tracking-wider text-xs">Education</h2>
            <p className="font-bold text-slate-800 italic text-sm">BCA (Computers)</p>
            <p className="text-sm text-slate-600 italic">YCMOU, Nashik</p>
          </section>
        </div>

        <div className="md:col-span-2 space-y-12">
          {/* Technical Environment Grid */}
        <section>
          <h2 className="text-2xl font-bold mb-8 flex items-center gap-2 text-slate-800">
            <Cpu className="text-blue-600" /> Technical Environment
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.techStack.map((item, i) => (
              <div key={i} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <h3 className="text-blue-600 font-bold text-xs uppercase tracking-widest mb-2">{item.category}</h3>
                <p className="text-slate-700 text-sm leading-relaxed">{item.tools}</p>
              </div>
            ))}
          </div>
        </section>
          <section>
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Server className="text-blue-600" /> Work Experience
            </h2>
            <div className="space-y-8">
              {data.experience.map((job, i) => (
                <div key={i} className="relative pl-6 border-l-2 border-slate-200">
                  <div className="absolute w-3 h-3 bg-blue-500 rounded-full -left-[7px] top-2 shadow-sm"></div>
                  <h3 className="text-xl font-bold text-slate-800">{job.role}</h3>
                  <p className="text-blue-600 font-semibold">{job.company} | <span className="text-slate-500 font-normal">{job.period}</span></p>
                  <p className="mt-2 text-slate-600 leading-relaxed text-sm">{job.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Shield className="text-blue-600" /> Key Projects
            </h2>
            <div className="space-y-4">
              {data.projects.map((proj, index) => (
                <div key={index} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden transition-all duration-300">
                  <button 
                    onClick={() => toggleProject(index)}
                    className="w-full text-left p-5 flex justify-between items-center hover:bg-slate-50 transition-colors focus:outline-none"
                  >
                    <div>
                      <h3 className="font-bold text-lg text-slate-900">{proj.title}</h3>
                      <p className="text-xs text-slate-400 font-medium uppercase tracking-tight">{proj.date}</p>
                    </div>
                    <div className="text-blue-500">
                      {expandedProject === index ? <ChevronUp size={24}/> : <ChevronDown size={24}/>}
                    </div>
                  </button>

                  <div className={`px-5 transition-all duration-300 ease-in-out ${
                    expandedProject === index ? "max-h-[500px] pb-5 opacity-100" : "max-h-0 opacity-0 invisible"
                  }`}>
                    <p className="text-slate-700 mb-4 font-medium text-sm border-l-4 border-blue-200 pl-3">{proj.short}</p>
                    <ul className="space-y-2 list-disc list-inside text-slate-600 text-sm">
                      {proj.details.map((detail, i) => (
                        <li key={i} className="leading-relaxed pl-1">{detail}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>

      <footer className="bg-slate-100 py-8 text-center text-slate-400 text-xs border-t border-slate-200">
        <p>© 2026 {data.name} • Professional Portfolio • Built with React & Tailwind CSS • Powered by Cloudflare Pages</p>
      </footer>
    </div>
  );
};

export default App;